// src/lib/ml-draw/ml-draw.js
//
// Patched to add LineString drawing alongside polygon/rectangle/circle.
//
// New public methods: startLine(), completeLine(lastPoint)
// New mode: this.mode === "line"
// New helper: _updateLinePreview(cursorPoint)
//
// LineStrings do not get TRANSFORM handles (move/scale/rotate) — those
// remain polygon-only, since they act on the whole shape off a centroid.
//
// LineStrings DO get vertex editing: drag a vertex, right-click one to
// delete, click a segment to insert. Every coordinate access on that path
// goes through _editCoords(), which reports whether the geometry is a
// closed ring, because that single fact drives all of the index arithmetic
// — handle count, minimum vertex count, and whether vertex 0 has a closing
// duplicate that must be kept in step.
 
// ── ONE INSTANCE PER MAP. NOT PER PAGE — PER MAP. ───────────────────────────
//
// The three source ids below are assigned CONSTANT STRINGS, and the nine layer
// ids are STRING LITERALS in both addLayer (:72-190) and destroy (:243-256).
// The constructor takes only onCreate and onUpdate; there is no option to
// namespace them.
//
// So a second instance on the SAME map throws on addSource("ml-draw-temp"),
// and -- less obviously -- even overriding this.tempSourceId / finalSourceId /
// handleSourceId would not help, because destroy() removes layers by literal
// and would tear down the first instance's.
//
// Two instances on two DIFFERENT maps are fine: each map has its own style and
// source registry. That is the shape the Operator Portal uses -- the submitter's
// map and the reviewer's map are chosen by role and only one is ever mounted.
//
// Recorded here because this is where someone about to add a second instance
// would look, and the failure is a throw at addSource that reads as a MapLibre
// problem rather than as a design constraint. Measured 2026-09-17; REM-367's
// neighbour in kind, a property nothing asserts.
export default class MlDraw {
    constructor(opts = {}) {
        this.map = null;
 
        // draw state
        this.mode = null;          // "polygon" | "rectangle" | "circle" | "line"
        this.drawing = false;
 
        this.points = [];
        this.centerPoint = null;
        this.cursorPoint = null;
        this.tempCoords = [];
        this.lastFeature = null;
 
        this.onCreateCallback = opts.onCreate || null;
        this.onUpdate = opts.onUpdate || null;
 
        this.tempSourceId = "ml-draw-temp";
        this.finalSourceId = "ml-draw-final";
        this.handleSourceId = "ml-draw-handles";
 
        // active transform (handle drag)
        this.transform = null;
        this._cursorBeforeTransform = null;
        this._finalFC = {
            type: "FeatureCollection",
            features: []
        };

        // Phase 1 vertex-edit. Orthogonal to draw mode and to the
        // transform handle display. When true, _buildHandleFeatures
        // emits per-vertex handles instead of the move/scale/rotate
        // bbox-transform handles (mutually exclusive — they'd collide
        // visually at corners).
        this.vertexEditMode = false;
    }
 
    // ─────────────────────────────────────────────
    // MAPBOX-LIKE CONTROL HOOKS
    // ─────────────────────────────────────────────
    onAdd(map) {
        this.map = map;
 
        // TEMP LAYER FOR PREVIEW
        map.addSource(this.tempSourceId, {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: []
            }
        });
 
        map.addLayer({
            id: "ml-draw-temp-fill",
            type: "fill",
            source: this.tempSourceId,
            paint: {
                "fill-color": "#ff6600",
                "fill-opacity": 0.2
            }
        });
 
        map.addLayer({
            id: "ml-draw-temp-line",
            type: "line",
            source: this.tempSourceId,
            paint: {
                "line-color": "#ff6600",
                "line-width": 2
            }
        });
 
        // FINAL AOI LAYER
        map.addSource(this.finalSourceId, {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: []
            }
        });
 
        map.addLayer({
            id: "ml-draw-final-fill",
            type: "fill",
            source: this.finalSourceId,
            paint: {
                "fill-color": "#00bcd4",
                "fill-opacity": 0.25
            }
        });
 
        map.addLayer({
            id: "ml-draw-final-line",
            type: "line",
            source: this.finalSourceId,
            paint: {
                "line-color": "#00bcd4",
                "line-width": 2
            }
        });
 
        // HANDLE SOURCE + LAYERS
        map.addSource(this.handleSourceId, {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: []
            }
        });
 
        map.addLayer({
            id: "ml-draw-handle-move",
            type: "circle",
            source: this.handleSourceId,
            filter: ["==", ["get", "handleKind"], "move"],
            paint: {
                "circle-radius": 7,
                "circle-color": "rgba(0,0,0,0)",
                "circle-stroke-color": "#16f2d4",
                "circle-stroke-width": 2
            }
        });
 
        map.addLayer({
            id: "ml-draw-handle-scale",
            type: "circle",
            source: this.handleSourceId,
            filter: ["==", ["get", "handleKind"], "scale"],
            paint: {
                "circle-radius": 5,
                "circle-color": "#16f2d4",
                "circle-stroke-color": "#050711",
                "circle-stroke-width": 1.5
            }
        });
 
        map.addLayer({
            id: "ml-draw-handle-rotate",
            type: "symbol",
            source: this.handleSourceId,
            filter: ["==", ["get", "handleKind"], "rotate"],
            layout: {
                "text-field": "◆",
                "text-size": 18,
                "text-allow-overlap": true
            },
            paint: {
                "text-color": "#16f2d4",
                "text-halo-color": "#050711",
                "text-halo-width": 1.2
            }
        });

        // Phase 1 vertex-edit layers. Halo first so it renders below
        // the vertex circle; vertex layer is the topmost handle layer
        // so its tap target sits above both halos and any underlying
        // line/fill (clicks on the vertex go to vertex-mode, not edge-
        // insert; see _onVertexEditLineClick guard).
        map.addLayer({
            id: "ml-draw-handle-vertex-halo",
            type: "circle",
            source: this.handleSourceId,
            filter: ["==", ["get", "handleKind"], "vertex"],
            paint: {
                "circle-radius": 10,
                "circle-color": "#f59e0b",
                "circle-opacity": 0.25
            }
        });

        map.addLayer({
            id: "ml-draw-handle-vertex",
            type: "circle",
            source: this.handleSourceId,
            filter: ["==", ["get", "handleKind"], "vertex"],
            paint: {
                "circle-radius": 5,
                "circle-color": "#f59e0b",
                "circle-stroke-color": "#ffffff",
                "circle-stroke-width": 2
            }
        });

        // DRAW EVENTS
        map.on("click", (e) => this._click(e));
        map.on("mousemove", (e) => this._mousemove(e));
        map.on("dblclick", (e) => this._dblclick(e));

        // HANDLE EVENTS
        const handleDown = (e) => this._onHandleDown(e);
        const handleHover = (e) => this._onHandleHover(e);
        const handleLeave = () => this._onHandleLeave();

        map.on("mousemove", "ml-draw-handle-move", handleHover);
        map.on("mousemove", "ml-draw-handle-scale", handleHover);
        map.on("mousemove", "ml-draw-handle-rotate", handleHover);
        map.on("mousemove", "ml-draw-handle-vertex", handleHover);

        map.on("mouseleave", "ml-draw-handle-move", handleLeave);
        map.on("mouseleave", "ml-draw-handle-scale", handleLeave);
        map.on("mouseleave", "ml-draw-handle-rotate", handleLeave);
        map.on("mouseleave", "ml-draw-handle-vertex", handleLeave);

        map.on("mousedown", "ml-draw-handle-move", handleDown);
        map.on("mousedown", "ml-draw-handle-scale", handleDown);
        map.on("mousedown", "ml-draw-handle-rotate", handleDown);
        map.on("mousedown", "ml-draw-handle-vertex", handleDown);

        map.on("mousemove", (e) => this._onHandleDrag(e));
        map.on("mouseup", () => this._onHandleUp());

        // Vertex-edit specific bindings. contextmenu on the vertex
        // layer = right-click-remove; click on the polygon line layer
        // (in vertex mode) = insert vertex at cursor position.
        map.on("contextmenu", "ml-draw-handle-vertex", (e) => this._onVertexContextMenu(e));
        map.on("click", "ml-draw-final-line", (e) => this._onVertexEditLineClick(e));

        return document.createElement("div");
    }
 
    onRemove() {
        if (!this.map) return;
        const map = this.map;
 
        if (map.getLayer("ml-draw-temp-fill")) map.removeLayer("ml-draw-temp-fill");
        if (map.getLayer("ml-draw-temp-line")) map.removeLayer("ml-draw-temp-line");
        if (map.getSource(this.tempSourceId)) map.removeSource(this.tempSourceId);
 
        if (map.getLayer("ml-draw-final-fill")) map.removeLayer("ml-draw-final-fill");
        if (map.getLayer("ml-draw-final-line")) map.removeLayer("ml-draw-final-line");
        if (map.getSource(this.finalSourceId)) map.removeSource(this.finalSourceId);
 
        if (map.getLayer("ml-draw-handle-vertex")) map.removeLayer("ml-draw-handle-vertex");
        if (map.getLayer("ml-draw-handle-vertex-halo")) map.removeLayer("ml-draw-handle-vertex-halo");
        if (map.getLayer("ml-draw-handle-move")) map.removeLayer("ml-draw-handle-move");
        if (map.getLayer("ml-draw-handle-scale")) map.removeLayer("ml-draw-handle-scale");
        if (map.getLayer("ml-draw-handle-rotate")) map.removeLayer("ml-draw-handle-rotate");
        if (map.getSource(this.handleSourceId)) map.removeSource(this.handleSourceId);
 
        this.map = null;
        this.transform = null;
    }
 
    // ─────────────────────────────────────────────
    // PUBLIC API (toolbar)
    // ─────────────────────────────────────────────
    startPolygon() {
        this.mode = "polygon";
        this._begin();
    }
 
    completePolygon(lastPoint) {
        if (!this.tempCoords || this.tempCoords.length < 2) return null;
 
        const pts = [...this.tempCoords];
 
        if (lastPoint) {
            pts.push(lastPoint);
        } else if (this.cursorPoint) {
            pts.push(this.cursorPoint);
        }
 
        const ring = [...pts, pts[0]];
 
        const feature = {
            type: "Feature",
            geometry: { type: "Polygon", coordinates: [ring] },
            properties: {}
        };
 
        this._commit(feature);
        return feature;
    }
 
    startRectangle() {
        this.mode = "rectangle";
        this._begin();
    }
 
    startCircle() {
        this.mode = "circle";
        this._begin();
    }
 
    // ─── NEW: line drawing ───
    startLine() {
        this.mode = "line";
        this._begin();
    }
 
    completeLine(lastPoint) {
        // need at least 1 clicked vertex; final click adds 2nd point
        if (!this.tempCoords || this.tempCoords.length < 1) return null;
 
        const pts = [...this.tempCoords];
 
        if (lastPoint) {
            pts.push(lastPoint);
        } else if (this.cursorPoint) {
            pts.push(this.cursorPoint);
        }
 
        if (pts.length < 2) return null;
 
        const feature = {
            type: "Feature",
            geometry: { type: "LineString", coordinates: pts },
            properties: {}
        };
 
        this._commit(feature);
        return feature;
    }
 
    stopDrawing() {
        this.mode = null;
        this.drawing = false;
 
        this.points = [];
        this.tempCoords = [];
        this.cursorPoint = null;
        this.centerPoint = null;
 
        this._clearTemp();
        this._resetCursor();
    }
 
    add(feature) {
        if (!this.map) return;
 
        this._setFinalFeature(feature);
        this.lastFeature = feature;
        this._updateHandles(feature);
        if (this.onUpdate) this.onUpdate();
    }
 
    delete() {
        if (!this.map) return;
 
        this._setFinalFeature(null);
        this.lastFeature = null;
        this._clearHandles();
        if (this.onUpdate) this.onUpdate();
    }
 
    getAll() {
        return this._finalFC || {
            type: "FeatureCollection",
            features: []
        };
    }
 
    setHandleMode(mode) {
        this.handleMode = mode;
    }
 
    // ─────────────────────────────────────────────
    // DRAW LIFECYCLE
    // ─────────────────────────────────────────────
    _begin() {
        if (!this.map) return;
 
        this.drawing = true;
        this.points = [];
        this.tempCoords = [];
        this.cursorPoint = null;
        this.centerPoint = null;
 
        this.map.getCanvas().style.cursor = "crosshair";
        this._clearTemp();
    }
 
    _click(e) {
        if (this.transform) return;
        if (!this.drawing) return;
 
        const lngLat = [e.lngLat.lng, e.lngLat.lat];
 
        // POLYGON
        if (this.mode === "polygon") {
            this.points.push(lngLat);
            this._updatePolygonPreview();
            return;
        }
 
        // RECTANGLE
        if (this.mode === "rectangle") {
            if (this.points.length === 0) {
                this.points = [lngLat];
            } else {
                const rect = this._rectangleFromTwoPoints(this.points[0], lngLat);
                this._commit(rect);
            }
            return;
        }
 
        // CIRCLE
        if (this.mode === "circle") {
            if (!this.centerPoint) {
                this.centerPoint = lngLat;
            } else {
                const circle = this._circleFromRadius(this.centerPoint, lngLat);
                this._commit(circle);
            }
            return;
        }
 
        // ─── NEW: LINE ───
        if (this.mode === "line") {
            this.points.push(lngLat);
            this._updateLinePreview();
            return;
        }
    }
 
    _mousemove(e) {
        if (!this.drawing) return;
 
        this.cursorPoint = [e.lngLat.lng, e.lngLat.lat];
        const lngLat = this.cursorPoint;
 
        if (this.mode === "polygon" && this.points.length > 0) {
            this._updatePolygonPreview(lngLat);
        }
 
        if (this.mode === "rectangle" && this.points.length === 1) {
            const rect = this._rectangleFromTwoPoints(this.points[0], lngLat);
            this._updateTemp(rect);
        }
 
        if (this.mode === "circle" && this.centerPoint) {
            const circ = this._circleFromRadius(this.centerPoint, lngLat);
            this._updateTemp(circ);
        }
 
        // ─── NEW: LINE preview ───
        if (this.mode === "line" && this.points.length > 0) {
            this._updateLinePreview(lngLat);
        }
    }
 
    _dblclick() {
        // intentionally disabled – we use right-click to close polygon/line
    }
 
    // ─────────────────────────────────────────────
    // GEOMETRY HELPERS
    // ─────────────────────────────────────────────
    _rectangleFromTwoPoints(a, b) {
        return {
            type: "Feature",
            geometry: {
                type: "Polygon",
                coordinates: [[
                    [a[0], a[1]],
                    [b[0], a[1]],
                    [b[0], b[1]],
                    [a[0], b[1]],
                    [a[0], a[1]]
                ]]
            },
            properties: {}
        };
    }
 
    _circleFromRadius(center, edge) {
        const [cx, cy] = center;
        const [ex, ey] = edge;
 
        const radius = Math.sqrt(
            Math.pow(ex - cx, 2) + Math.pow(ey - cy, 2)
        );
 
        const steps = 64;
        const coords = [];
 
        for (let i = 0; i <= steps; i++) {
            const angle = (i / steps) * Math.PI * 2;
            coords.push([
                cx + radius * Math.cos(angle),
                cy + radius * Math.sin(angle)
            ]);
        }
 
        return {
            type: "Feature",
            geometry: { type: "Polygon", coordinates: [coords] },
            properties: {}
        };
    }
 
    // ─────────────────────────────────────────────
    // INTERNAL – TEMP + FINAL
    // ─────────────────────────────────────────────
    _updatePolygonPreview(cursorPoint = null) {
        const base = [...this.points];
 
        let preview = [...base];
        if (cursorPoint) preview.push(cursorPoint);
 
        if (preview.length > 2) preview.push(base[0]);
 
        this.tempCoords = [...base];
 
        this._updateTemp({
            type: "Feature",
            geometry: { type: "LineString", coordinates: preview },
            properties: {}
        });
    }
 
    // ─── NEW: line preview ───
    _updateLinePreview(cursorPoint = null) {
        const base = [...this.points];
 
        let preview = [...base];
        if (cursorPoint) preview.push(cursorPoint);
 
        // committed vertices (those actually clicked)
        this.tempCoords = [...base];
 
        // need ≥ 2 points to render a line; before second point, just clear
        if (preview.length < 2) {
            this._clearTemp();
            return;
        }
 
        this._updateTemp({
            type: "Feature",
            geometry: { type: "LineString", coordinates: preview },
            properties: {}
        });
    }
 
    _updateTemp(feature) {
        const src = this.map?.getSource(this.tempSourceId);
        if (!src) return;
 
        src.setData({
            type: "FeatureCollection",
            features: feature ? [feature] : []
        });
    }
 
    _clearTemp() {
        this._updateTemp(null);
    }
 
    _setFinalFeature(feature) {
        const fc = feature
            ? { type: "FeatureCollection", features: [feature] }
            : { type: "FeatureCollection", features: [] };
 
        const src = this.map?.getSource(this.finalSourceId);
        if (src) src.setData(fc);
        this._finalFC = fc;
    }
 
    _commit(feature) {
        this.lastFeature = feature;
 
        this._setFinalFeature(feature);
        this._clearTemp();
 
        // _updateHandles only paints handles for Polygons (returns early
        // for non-Polygon geometries). Lines get no handles, which is
        // intentional — we delete-and-redraw rather than edit.
        this._updateHandles(feature);
 
        if (this.onCreateCallback) this.onCreateCallback(feature);
        if (this.onUpdate) this.onUpdate(this.lastFeature);
 
        this.drawing = false;
        this.mode = null;
        this._resetCursor();
    }
 
    _resetCursor() {
        if (this.map && !this.transform) {
            this.map.getCanvas().style.cursor = "grab";
        }
    }
 
    // ─────────────────────────────────────────────
    // HANDLE MANAGEMENT
    // ─────────────────────────────────────────────
    _clearHandles() {
        const src = this.map?.getSource(this.handleSourceId);
        if (!src) return;
        src.setData({
            type: "FeatureCollection",
            features: []
        });
    }
 
    _updateHandles(feature) {
        if (!this.map) return;
        const src = this.map.getSource(this.handleSourceId);
        if (!src || !feature || !feature.geometry) {
            this._clearHandles();
            return;
        }
 
        // Vertex edit applies to polygons AND lines. The bbox transform
        // handles below stay polygon-only: they move, scale and rotate the
        // whole shape off a centroid, which is a polygon gesture.
        if (this.vertexEditMode) {
            const edit = this._editCoords(feature);
            if (!edit || edit.coords.length < 2) {
                this._clearHandles();
                return;
            }
            src.setData({
                type: "FeatureCollection",
                features: this._buildVertexHandleFeatures(edit.coords, edit.closed)
            });
            return;
        }

        const coords = this._getPolygonCoords(feature);
        if (!coords || coords.length === 0) {
            this._clearHandles();
            return;
        }

        const center = this._computeCentroid(coords);
        const bbox = this._computeBBox(coords);
        const features = this._buildHandleFeatures(center, bbox);

        src.setData({
            type: "FeatureCollection",
            features
        });
    }
 
    _getPolygonCoords(feature) {
        if (!feature?.geometry) return null;
        const geom = feature.geometry;
        if (geom.type === "Polygon") {
            return geom.coordinates[0];
        }
        // LineString / MultiPolygon / etc. → no BBOX TRANSFORM handles.
        // Vertex editing is a separate question — see _editCoords.
        return null;
    }

    // The coordinate array vertex editing operates on, for either geometry
    // it supports, plus the one fact that separates them.
    //
    // `closed` marks the GeoJSON Polygon convention of repeating vertex 0 as
    // the final entry so the ring closes. Every index and length rule in the
    // vertex code turns on that single difference: how many handles to draw,
    // what the minimum vertex count is, and whether moving or deleting
    // vertex 0 has to be mirrored at the end of the array. A LineString has
    // no such duplicate, and its endpoints are genuinely independent.
    //
    // Returns the LIVE array off the passed feature, so callers that intend
    // to mutate should pass a clone — which the drag, delete and insert
    // paths all already do.
    _editCoords(feature) {
        const geom = feature?.geometry;
        if (!geom) return null;
        if (geom.type === "Polygon") {
            return { coords: geom.coordinates[0], closed: true };
        }
        if (geom.type === "LineString") {
            return { coords: geom.coordinates, closed: false };
        }
        return null;
    }
 
    _computeCentroid(coords) {
        let sumX = 0;
        let sumY = 0;
        const n = coords.length;
        for (let i = 0; i < n; i++) {
            sumX += coords[i][0];
            sumY += coords[i][1];
        }
        return [sumX / n, sumY / n];
    }
 
    _computeBBox(coords) {
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;
 
        coords.forEach(([x, y]) => {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
        });
 
        return { minX, maxX, minY, maxY };
    }
 
    // BBOX TRANSFORM handles only. The vertex-edit branch that used to sit
    // here moved up into _updateHandles, which is the only caller and now
    // decides between the two modes — vertex handles were being built in two
    // places once lines entered the picture, and only one of them knew
    // whether the geometry was closed.
    _buildHandleFeatures(center, bbox) {
        const { minX, maxX, minY, maxY } = bbox;
        const [cx, cy] = center;

        const features = [];
 
        features.push({
            type: "Feature",
            geometry: { type: "Point", coordinates: [cx, cy] },
            properties: { handleKind: "move" }
        });
 
        const corners = [
            [minX, maxY],
            [maxX, maxY],
            [maxX, minY],
            [minX, minY]
        ];
 
        corners.forEach((coord, idx) => {
            features.push({
                type: "Feature",
                geometry: { type: "Point", coordinates: coord },
                properties: {
                    handleKind: "scale",
                    scaleKind: "corner",
                    cornerIndex: idx
                }
            });
        });
 
        const topMid = [(minX + maxX) / 2, maxY];
        const rightMid = [maxX, (minY + maxY) / 2];
        const bottomMid = [(minX + maxX) / 2, minY];
        const leftMid = [minX, (minY + maxY) / 2];
 
        [
            { coord: topMid, edgePos: "top" },
            { coord: rightMid, edgePos: "right" },
            { coord: bottomMid, edgePos: "bottom" },
            { coord: leftMid, edgePos: "left" }
        ].forEach((edge) => {
            features.push({
                type: "Feature",
                geometry: { type: "Point", coordinates: edge.coord },
                properties: {
                    handleKind: "scale",
                    scaleKind: "edge",
                    edgePos: edge.edgePos
                }
            });
        });
 
        if (this.map && topMid) {
            const topLatLng = { lng: topMid[0], lat: topMid[1] };
            const screenPt = this.map.project(topLatLng);
            screenPt.y -= 30;
            const rotatedLngLat = this.map.unproject(screenPt);
 
            features.push({
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: [rotatedLngLat.lng, rotatedLngLat.lat]
                },
                properties: { handleKind: "rotate" }
            });
        }
 
        return features;
    }
 
    // ─────────────────────────────────────────────
    // HANDLE INTERACTION
    // ─────────────────────────────────────────────
    _onHandleHover(e) {
        if (this.transform) return;
        if (!e.features || !e.features.length) return;
 
        const handle = e.features[0];
        const kind = handle.properties?.handleKind;
        if (!this.map) return;
 
        if (kind === "move") {
            this._setCursor("move");
        } else if (kind === "scale") {
            const scaleKind = handle.properties?.scaleKind;
            const edgePos = handle.properties?.edgePos;
 
            if (scaleKind === "corner") {
                this._setCursor("nwse-resize");
            } else if (scaleKind === "edge") {
                if (edgePos === "top" || edgePos === "bottom") {
                    this._setCursor("ns-resize");
                } else {
                    this._setCursor("ew-resize");
                }
            }
        } else if (kind === "rotate") {
            this._setCursor("grab");
        } else if (kind === "vertex") {
            // "pointer" (the finger), not "grab". A vertex handle is a small
            // discrete target rather than a surface you pick up, and the
            // finger is what tells the user "this dot is hittable" — grab
            // reads as a pan affordance, which is what the rest of the map
            // already uses. The drag itself still flips to "grabbing" below,
            // so the pick-up feedback is unchanged.
            this._setCursor("pointer");
        }
    }

    _onHandleLeave() {
        if (this.transform) return;
        this._resetCursor();
    }
 
    _onHandleDown(e) {
        if (!this.map) return;
        if (!this.lastFeature) return;
        if (!e.features || !e.features.length) return;
 
        const handle = e.features[0];
        const props = handle.properties || {};
        const kind = props.handleKind;
        const lngLat = [e.lngLat.lng, e.lngLat.lat];
 
        // A vertex drag works on either geometry; the transform gestures
        // (move/scale/rotate) remain polygon-only, and their handles are
        // never emitted for a line, so this only has to admit the vertex
        // case. The centroid is unused by the vertex branch of the drag but
        // is still recorded, so it is computed from whatever coords exist.
        const coords = kind === "vertex"
            ? this._editCoords(this.lastFeature)?.coords
            : this._getPolygonCoords(this.lastFeature);
        if (!coords) return;
        const center = this._computeCentroid(coords);

        this.transform = {
            kind,
            scaleKind: props.scaleKind || null,
            cornerIndex: Number.isFinite(props.cornerIndex) ? props.cornerIndex : null,
            edgePos: props.edgePos || null,
            vertexIndex: Number.isFinite(props.vertexIndex) ? props.vertexIndex : null,
            startLngLat: lngLat,
            center,
            startFeature: JSON.parse(JSON.stringify(this.lastFeature)),
            rotateAngle0: null
        };

        if (kind === "rotate") {
            const [cx, cy] = center;
            const dx0 = lngLat[0] - cx;
            const dy0 = lngLat[1] - cy;
            this.transform.rotateAngle0 = Math.atan2(dy0, dx0);
            this._setCursor("grabbing");
        } else if (kind === "move" || kind === "vertex") {
            this._setCursor("grabbing");
        }
 
        if (this.map.dragPan && this.map.dragPan.disable) {
            this.map.dragPan.disable();
        }
    }
 
    _onHandleDrag(e) {
        if (!this.transform || !this.lastFeature) return;
        if (!this.map) return;
 
        this.drawing = false;
 
        const t = this.transform;
        const current = [e.lngLat.lng, e.lngLat.lat];
 
        const newFeature = JSON.parse(JSON.stringify(t.startFeature));
        const geom = newFeature.geometry;

        // A vertex drag is valid on a line; every other gesture here reads
        // coordinates[0] and reasons about a centroid, so those stay
        // polygon-only.
        const edit = this._editCoords(newFeature);
        if (!edit) return;
        if (t.kind !== "vertex" && geom.type !== "Polygon") return;

        const coords = edit.coords;
        const closed = edit.closed;

        if (t.kind === "move") {
            const dx = current[0] - t.startLngLat[0];
            const dy = current[1] - t.startLngLat[1];
 
            for (let i = 0; i < coords.length; i++) {
                coords[i] = [
                    coords[i][0] + dx,
                    coords[i][1] + dy
                ];
            }
 
        } else if (t.kind === "scale") {
            const [cx, cy] = t.center;
            const [sx0, sy0] = t.startLngLat;
            const [sx1, sy1] = current;
 
            let scaleX = 1;
            let scaleY = 1;
 
            if (t.scaleKind === "corner") {
                const vx0 = sx0 - cx;
                const vy0 = sy0 - cy;
                const vx1 = sx1 - cx;
                const vy1 = sy1 - cy;
 
                scaleX = vx0 !== 0 ? vx1 / vx0 : 1;
                scaleY = vy0 !== 0 ? vy1 / vy0 : 1;
            } else if (t.scaleKind === "edge") {
                if (t.edgePos === "top" || t.edgePos === "bottom") {
                    const dy0 = sy0 - cy;
                    const dy1 = sy1 - cy;
                    scaleY = dy0 !== 0 ? dy1 / dy0 : 1;
                    scaleX = 1;
                } else {
                    const dx0 = sx0 - cx;
                    const dx1 = sx1 - cx;
                    scaleX = dx0 !== 0 ? dx1 / dx0 : 1;
                    scaleY = 1;
                }
            }
 
            for (let i = 0; i < coords.length; i++) {
                const [x0, y0] = t.startFeature.geometry.coordinates[0][i];
                const dx0 = x0 - cx;
                const dy0 = y0 - cy;
 
                coords[i] = [
                    cx + dx0 * scaleX,
                    cy + dy0 * scaleY
                ];
            }
 
        } else if (t.kind === "rotate") {
            const [cx, cy] = t.center;
            const [rx1, ry1] = current;
            const angle1 = Math.atan2(ry1 - cy, rx1 - cx);
            const delta = angle1 - (t.rotateAngle0 || 0);

            for (let i = 0; i < coords.length; i++) {
                const [x0, y0] = t.startFeature.geometry.coordinates[0][i];
                const dx = x0 - cx;
                const dy = y0 - cy;

                const nx = dx * Math.cos(delta) - dy * Math.sin(delta);
                const ny = dx * Math.sin(delta) + dy * Math.cos(delta);

                coords[i] = [cx + nx, cy + ny];
            }
        } else if (t.kind === "vertex") {
            // Single-vertex move. Closed-ring guarantee: GeoJSON Polygons
            // repeat the first vertex at the end of the ring so the shape
            // closes. If vertex 0 is being dragged, the closing duplicate
            // must follow it — otherwise the ring tears open.
            //
            // A LineString has no duplicate and its first point is just a
            // point, so the mirror write is skipped. Doing it anyway would
            // drag the far endpoint along with the near one.
            if (Number.isFinite(t.vertexIndex)) {
                coords[t.vertexIndex] = [current[0], current[1]];
                if (closed && t.vertexIndex === 0) {
                    coords[coords.length - 1] = [current[0], current[1]];
                }
            }
        }

        this.lastFeature = newFeature;
        this._setFinalFeature(newFeature);
        this._updateHandles(newFeature);

        // Flag B: vertex drag suppresses per-frame onUpdate (fires on
        // drag end via _onHandleUp instead, like Path 1 spec). The
        // existing transform handles preserve their per-frame onUpdate
        // contract for current consumers (AOI editing, etc.).
        if (t.kind !== "vertex" && this.onUpdate) this.onUpdate();
    }
 
    _onHandleUp() {
        if (!this.transform) return;
 
        if (this.map?.dragPan && this.map.dragPan.enable) {
            this.map.dragPan.enable();
        }
 
        this.transform = null;
        this._resetCursor();
 
        if (this.onUpdate) this.onUpdate();
    }
 
    _setCursor(cursor) {
        if (!this.map) return;
        this.map.getCanvas().style.cursor = cursor;
    }

    // ─────────────────────────────────────────────
    // Phase 1 — VERTEX EDIT
    // ─────────────────────────────────────────────

    // Enter vertex-edit on the existing lastFeature. Suppresses bbox
    // transform handles, emits per-vertex handles. Consumer should
    // call add(feature) first to load a polygon they want to edit.
    enterVertexEdit() {
        if (!this.lastFeature) return false;
        this.vertexEditMode = true;
        this._updateHandles(this.lastFeature);
        return true;
    }

    exitVertexEdit() {
        this.vertexEditMode = false;
        if (this.lastFeature) {
            this._updateHandles(this.lastFeature);
        } else {
            this._clearHandles();
        }
    }

    // Per-vertex handle features. For a closed ring the final entry is a
    // duplicate of the first and gets no handle of its own — two handles on
    // one point, where dragging either moves the other, is indistinguishable
    // from a bug. The closed-ring guarantee is then maintained at drag time
    // (see _onHandleDrag vertex branch). An open line has no duplicate, so
    // every entry including the last endpoint gets a handle.
    _buildVertexHandleFeatures(coords, closed = true) {
        const ring = coords;
        const uniqueCount = closed ? ring.length - 1 : ring.length;
        const features = [];
        for (let i = 0; i < uniqueCount; i++) {
            features.push({
                type: "Feature",
                geometry: { type: "Point", coordinates: ring[i] },
                properties: {
                    handleKind: "vertex",
                    vertexIndex: i
                }
            });
        }
        return features;
    }

    // Right-click on a vertex → remove. Min-vertex guard per Flag E:
    // ring.length > 4 means strictly more than 3 unique vertices
    // (3 unique + 1 closing duplicate). Silently no-op at minimum.
    // Touch/mobile alternative deferred — desktop right-click only for v1.
    _onVertexContextMenu(e) {
        if (!this.vertexEditMode) return;
        if (!this.lastFeature) return;
        if (!e.features || !e.features.length) return;

        if (e.preventDefault) e.preventDefault();
        if (e.originalEvent?.preventDefault) e.originalEvent.preventDefault();

        const handle = e.features[0];
        const vertexIndex = handle.properties?.vertexIndex;
        if (!Number.isFinite(vertexIndex)) return;

        const edit = this._editCoords(this.lastFeature);
        if (!edit) return;

        // Minimum-vertex guard, which differs by geometry. A polygon needs
        // 3 unique vertices, held as 4 array entries with the closing
        // duplicate. A line needs 2 — delete below that and it is not a
        // line any more. Silently no-op at the minimum, as before.
        const minEntries = edit.closed ? 4 : 2;
        if (edit.coords.length <= minEntries) return;

        const newFeature = JSON.parse(JSON.stringify(this.lastFeature));
        const newRing = this._editCoords(newFeature).coords;
        newRing.splice(vertexIndex, 1);
        // Closed-ring guarantee: removing vertex 0 means the new vertex 0
        // must also become the closing duplicate. Open lines have neither.
        if (edit.closed && vertexIndex === 0) {
            newRing[newRing.length - 1] = [newRing[0][0], newRing[0][1]];
        }

        this.lastFeature = newFeature;
        this._setFinalFeature(newFeature);
        this._updateHandles(newFeature);

        if (this.onUpdate) this.onUpdate();
    }

    // Click on the polygon's outline (in vertex-edit mode) inserts a
    // new vertex at the cursor position. Skips when the click also
    // lands on a vertex handle (those clicks own the gesture as
    // mousedown→mouseup pairs that may have started a drag).
    _onVertexEditLineClick(e) {
        if (!this.vertexEditMode) return;
        if (!this.lastFeature) return;
        if (!this.map) return;

        // Vertex layer renders above the line; if the click coordinates
        // also hit a vertex handle, that's the user's gesture — not an
        // edge-insert. Catches the edge case of clicking on a vertex
        // that happens to sit exactly on the rendered line geometry.
        const vertexHits = this.map.queryRenderedFeatures(e.point, {
            layers: ["ml-draw-handle-vertex"]
        });
        if (vertexHits.length > 0) return;

        const edit = this._editCoords(this.lastFeature);
        if (!edit) return;

        const cursor = [e.lngLat.lng, e.lngLat.lat];
        const { insertAt } = this._closestSegment(edit.coords, cursor);

        const newFeature = JSON.parse(JSON.stringify(this.lastFeature));
        const newRing = this._editCoords(newFeature).coords;
        newRing.splice(insertAt, 0, [cursor[0], cursor[1]]);
        // No closing-ring adjustment: insertion never touches index 0
        // or the closing duplicate.

        this.lastFeature = newFeature;
        this._setFinalFeature(newFeature);
        this._updateHandles(newFeature);

        if (this.onUpdate) this.onUpdate();
    }

    // Find the segment closest to a point. Returns the index at which a new
    // vertex should be inserted (after segment start).
    //
    // Geometry-agnostic as written: a closed ring of N entries has N-1
    // segments (the last one being the closing edge, since entry N-1
    // duplicates entry 0), and an open line of N points also has N-1. Same
    // arithmetic, different reason.
    _closestSegment(ring, point) {
        const segCount = ring.length - 1;
        let bestI = 0;
        let bestDist = Infinity;
        for (let i = 0; i < segCount; i++) {
            const a = ring[i];
            const b = ring[i + 1];
            const d = this._distanceToSegment(point, a, b);
            if (d < bestDist) {
                bestDist = d;
                bestI = i;
            }
        }
        return { insertAt: bestI + 1, distance: bestDist };
    }

    // Perpendicular distance from p to segment ab, clamped to segment
    // endpoints. Lng/lat treated as planar — fine at finding-scale
    // (<150m typical); not a great-circle calculation.
    _distanceToSegment(p, a, b) {
        const ax = a[0], ay = a[1];
        const bx = b[0], by = b[1];
        const px = p[0], py = p[1];
        const dx = bx - ax;
        const dy = by - ay;
        if (dx === 0 && dy === 0) {
            const ddx = px - ax;
            const ddy = py - ay;
            return Math.sqrt(ddx * ddx + ddy * ddy);
        }
        const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy)));
        const qx = ax + t * dx;
        const qy = ay + t * dy;
        const ddx = px - qx;
        const ddy = py - qy;
        return Math.sqrt(ddx * ddx + ddy * ddy);
    }
}